import { useEffect, useState } from "react";
import {
  buildGenerator,
  fetchGenerators,
  fetchMaterials,
  fetchPrinters,
  type BuildResponse,
  type GeneratorInfo,
} from "../api";
import { Icon } from "../components/Icon";
import { ScoreGauge, StatusBadge } from "../components/ui";
import { useToast } from "../lib/toast";
import type { MaterialProfile, PrinterProfile } from "../types";

import { useT } from "../i18n";
export function GeneratorsPage() {
  const tr = useT();
  const [gens, setGens] = useState<GeneratorInfo[]>([]);
  const [printers, setPrinters] = useState<PrinterProfile[]>([]);
  const [materials, setMaterials] = useState<MaterialProfile[]>([]);
  const [printer, setPrinter] = useState("prusa_mk4_0.4");
  const [material, setMaterial] = useState("PLA");
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<BuildResponse | null>(null);
  const toast = useToast((s) => s.show);

  // Materials compatible with the selected printer.
  const compatMats = (() => {
    const p = printers.find((x) => x.id === printer);
    const ids = p?.compatible_materials ?? materials.map((m) => m.id);
    return materials.filter((m) => ids.includes(m.id));
  })();

  useEffect(() => {
    fetchGenerators().then(setGens).catch((e) => toast(String(e.message), "bad"));
    fetchPrinters().then((ps) => {
      setPrinters(ps);
      if (ps.length && !ps.some((p) => p.id === printer)) setPrinter(ps[0].id);
    }).catch(() => {});
    fetchMaterials().then(setMaterials).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  async function build(name: string) {
    setBusy(name);
    setResult(null);
    try {
      const r = await buildGenerator(name, { printer_profile: printer, material, params: {} });
      setResult(r);
      toast(r.passed ? "Validé par l'INSPECTOR" : "Construit mais non validé");
    } catch (e) {
      toast(String((e as Error).message), "bad");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>{tr("Création")}</div>
          <h1>{tr("Générateurs paramétriques")}</h1>
          <div className="sub">{tr("Pièces fonctionnelles, dimensionnées pour ta machine, validées par l'INSPECTOR.")}</div>
        </div>
        <div className="row">
          <select
            value={printer}
            onChange={(e) => {
              const np = e.target.value;
              setPrinter(np);
              const p = printers.find((x) => x.id === np);
              const ids = p?.compatible_materials ?? [];
              if (ids.length && !ids.includes(material)) setMaterial(ids[0]);
            }}
          >
            {printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={material} onChange={(e) => setMaterial(e.target.value)}>
            {compatMats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid cards">
        {gens.map((g) => {
          const params = Object.keys(g.params_schema?.properties ?? {});
          return (
            <div key={g.name} className="card">
              <div className="row between" style={{ marginBottom: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, display: "grid", placeItems: "center", color: "var(--accent-3)", background: "var(--accent-soft)", border: "1px solid var(--accent-line)" }}>
                  <Icon name="generators" size={17} />
                </div>
                <span className="id">{g.name}</span>
              </div>
              <div className="card-title">{g.title}</div>
              <div className="muted" style={{ margin: "7px 0 12px", fontSize: 12.5, minHeight: 48, lineHeight: 1.5 }}>{g.description}</div>
              <div className="row wrap" style={{ gap: 5, marginBottom: 14 }}>
                {params.slice(0, 5).map((p) => <span key={p} className="kbd">{p}</span>)}
              </div>
              <button className="btn primary" style={{ width: "100%", justifyContent: "center" }} disabled={busy === g.name} onClick={() => build(g.name)}>
                {busy === g.name ? <><span className="spinner" /> Construction…</> : <><Icon name="zap" size={15} /> Construire + valider</>}
              </button>
            </div>
          );
        })}
      </div>

      {result && (
        <div className="card" style={{ marginTop: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 28, alignItems: "center" }}>
            <div style={{ textAlign: "center", paddingRight: 28, borderRight: "1px solid var(--line)" }}>
              <ScoreGauge value={result.score} size={108} label="SCORE" />
              <div style={{ marginTop: 14 }}><StatusBadge passed={result.passed} /></div>
              <div className="eyebrow" style={{ marginTop: 12 }}>INSPECTOR · {result.generator}</div>
            </div>
            <div>
              <div className="row between" style={{ marginBottom: 8 }}>
                <h2>{tr("Rapport de printabilité")}</h2>
              </div>
              <div className="checks">
                {result.checks.map((c) => {
                  const k = c.status === "pass" ? "pass" : c.status === "fail" ? "fail" : "warn";
                  return (
                    <div key={c.name} className="check-row">
                      <div className={`check-ico ${k}`}>
                        <Icon name={c.status === "pass" ? "check" : c.status === "fail" ? "x" : "alert"} size={14} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="check-name">{c.name}</div>
                        <div className="check-detail">{c.detail}</div>
                      </div>
                      <div className="check-val">{c.measured != null ? c.measured.toFixed(2) : c.status}</div>
                    </div>
                  );
                })}
              </div>
              {result.critique.length > 0 && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: "var(--r-sm)" }}>
                  <div className="eyebrow" style={{ color: "var(--warn)", marginBottom: 7 }}>{tr("Critique de conception")}</div>
                  {result.critique.map((c, i) => (
                    <div key={i} className="muted" style={{ fontSize: 12.5, padding: "2px 0", color: "var(--text-2)" }}>• {c}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
